import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MoreVertical, X } from "lucide-react";
import { toast } from "react-hot-toast";
import PolicyFormWizardShell from "../policy-wizard/PolicyFormWizardShell";
import PolicyWorkflowStepSection from "../policy-wizard/PolicyWorkflowStepSection";
import PolicyApplicabilityStepSection from "../policy-wizard/PolicyApplicabilityStepSection";
import PolicyListToolbar from "../policy-wizard/PolicyListToolbar";
import { policyListRowActionClass, policyListStatusClass } from "../policy-wizard/PolicyListTableHeader";
import { PolicyHintField } from "../policy-wizard/PolicyFieldHint";
import { LOAN_POLICY_STEPS } from "../policy-wizard/policyWizardSteps";
import {
  createPolicyWizardNav,
  validateApplicabilityStep,
  validateBasicsStep,
  validateWorkflowStep,
} from "../policy-wizard/policyWizardValidation";
import usePolicyCompanyRoles from "../policy-wizard/usePolicyCompanyRoles";
import { payrollPolicyService } from "../../../services/api";
import {
  LOAN_APPROVAL_METHODS,
  LOAN_WORKFLOW_TABS,
  packAssignedTo,
  pwInput,
  unpackAssignedTo,
} from "./payrollJsonPolicyForm";

const LOAN_TYPES = [
  { value: "personal", label: "Personal" },
  { value: "medical", label: "Medical" },
  { value: "education", label: "Education" },
  { value: "housing", label: "Housing (employer)" },
  { value: "vehicle", label: "Vehicle" },
  { value: "other", label: "Other" },
];

function emptyLoanForm() {
  return {
    policyName: "",
    status: "active",
    effectiveDate: new Date().toISOString().slice(0, 10),
    loanType: "personal",
    allEmployees: true,
    branch: "",
    department: "",
    designation: "",
    grade: "",
    category: "",
    subDepartment: "",
    location: "",
    employmentTypes: [],
    individualEmployeeIds: "",
    approvalMethod: "workflow",
    workflowSteps: [],
    reminderDays: 3,
    minServiceMonths: 12,
    salaryBasis: "basic",
    minAmount: 0,
    maxAmount: "",
    limitMode: "amount",
    salaryMultiple: 1,
    salaryPercent: 50,
    maxOutstandingLoans: 1,
    maxEmiAmount: "",
    minTenureMonths: 6,
    maxTenureMonths: 36,
    interestMode: "interest_free",
    interestRateAnnual: 0,
    concessionRateAnnual: "",
    processingFeeMode: "none",
    processingFee: 0,
    firstDeductionDelayMonths: 1,
    onInsufficientSalary: "partial",
    allowMoratorium: false,
    principalRecovery: "",
    interestRecovery: "",
    additionalRecovery: "",
    fnfRecovery: "",
    perquisite: "",
    processingFeeMap: "",
    chargePerquisite: false,
    benchmarkRateAnnual: "",
    purposeNote: "",
  };
}

function rowToForm(row) {
  const r = row.rulesJson || {};
  const w = row.workflowJson || {};
  const p = row.payrollMapJson || {};
  const t = row.taxJson || {};
  return {
    ...emptyLoanForm(),
    ...unpackAssignedTo(row.assignedTo),
    id: row.id,
    policyName: row.name || "",
    status: row.status || "active",
    effectiveDate: String(row.effectiveDate || "").slice(0, 10),
    loanType: row.loanType || r.loanType || "personal",
    approvalMethod: w.approvalMethod || "workflow",
    workflowSteps: Array.isArray(w.workflowSteps) ? w.workflowSteps : [],
    reminderDays: w.reminderDays || 3,
    minServiceMonths: r.minServiceMonths ?? 12,
    salaryBasis: r.salaryBasis || "basic",
    minAmount: r.minAmount ?? 0,
    maxAmount: r.maxAmount ?? "",
    limitMode: r.limitMode || "amount",
    salaryMultiple: r.salaryMultiple ?? 1,
    salaryPercent: r.salaryPercent ?? 50,
    maxOutstandingLoans: r.maxOutstandingLoans ?? 1,
    maxEmiAmount: r.maxEmiAmount ?? "",
    minTenureMonths: r.minTenureMonths ?? 6,
    maxTenureMonths: r.maxTenureMonths ?? 36,
    interestMode: r.interestMode || "interest_free",
    interestRateAnnual: r.interestRateAnnual ?? 0,
    concessionRateAnnual: r.concessionRateAnnual ?? "",
    processingFeeMode: r.processingFeeMode || "none",
    processingFee: r.processingFee ?? 0,
    firstDeductionDelayMonths: r.firstDeductionDelayMonths ?? 1,
    onInsufficientSalary: r.recovery?.onInsufficientSalary || "partial",
    allowMoratorium: !!r.recovery?.allowMoratorium,
    principalRecovery: p.principalRecovery || "",
    interestRecovery: p.interestRecovery || "",
    additionalRecovery: p.additionalRecovery || "",
    fnfRecovery: p.fnfRecovery || "",
    perquisite: p.perquisite || "",
    processingFeeMap: p.processingFee || "",
    chargePerquisite: !!t.chargePerquisite,
    benchmarkRateAnnual: t.benchmarkRateAnnual ?? "",
    purposeNote: t.purposeNote || "",
  };
}

function formToPayload(form) {
  return {
    id: form.id,
    name: form.policyName,
    status: form.status,
    effectiveDate: form.effectiveDate,
    loanType: form.loanType,
    assignedTo: packAssignedTo(form),
    rulesJson: {
      loanType: form.loanType,
      minServiceMonths: Number(form.minServiceMonths) || 0,
      salaryBasis: form.salaryBasis,
      minAmount: Number(form.minAmount) || 0,
      maxAmount: form.maxAmount === "" ? null : Number(form.maxAmount),
      limitMode: form.limitMode,
      salaryMultiple: Number(form.salaryMultiple) || 1,
      salaryPercent: Number(form.salaryPercent) || 0,
      maxOutstandingLoans: Number(form.maxOutstandingLoans) || 1,
      maxEmiAmount: form.maxEmiAmount === "" ? null : Number(form.maxEmiAmount),
      minTenureMonths: Number(form.minTenureMonths) || 1,
      maxTenureMonths: Number(form.maxTenureMonths) || 1,
      interestMode: form.interestMode,
      interestRateAnnual: Number(form.interestRateAnnual) || 0,
      concessionRateAnnual:
        form.concessionRateAnnual === "" ? null : Number(form.concessionRateAnnual),
      processingFeeMode: form.processingFeeMode,
      processingFee: Number(form.processingFee) || 0,
      firstDeductionDelayMonths: Number(form.firstDeductionDelayMonths) || 0,
      recovery: {
        onInsufficientSalary: form.onInsufficientSalary,
        allowMoratorium: !!form.allowMoratorium,
      },
    },
    workflowJson: {
      approvalMethod: form.approvalMethod,
      workflowSteps: form.workflowSteps || [],
      reminderDays: Number(form.reminderDays) || 3,
    },
    payrollMapJson: {
      principalRecovery: form.principalRecovery,
      interestRecovery: form.interestRecovery,
      additionalRecovery: form.additionalRecovery,
      fnfRecovery: form.fnfRecovery,
      perquisite: form.perquisite,
      processingFee: form.processingFeeMap,
    },
    taxJson: {
      chargePerquisite: !!form.chargePerquisite,
      benchmarkRateAnnual:
        form.benchmarkRateAnnual === "" ? null : Number(form.benchmarkRateAnnual),
      purposeNote: form.purposeNote,
    },
  };
}

export default function LoanPolicyComponent({
  darkMode = false,
  canEdit = true,
  branchesList = [],
  departmentsList = [],
  designationsList = [],
  gradesList = [],
  categoriesList = [],
  subDepartmentsList = [],
}) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState(emptyLoanForm);
  const [step, setStep] = useState(1);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [workflowTab, setWorkflowTab] = useState("approval");
  const [actionMenu, setActionMenu] = useState(null);
  const { policyRoleOptions, defaultPolicyRoleValue } = usePolicyCompanyRoles();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await payrollPolicyService.listLoanPolicies();
      setPolicies(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load loan policies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const listed = useMemo(() => {
    const q = search.trim().toLowerCase();
    return policies.filter((p) => {
      if (!showInactive && p.status === "inactive") return false;
      if (q && !String(p.name || "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [policies, showInactive, search]);

  const validateStep = (n) => {
    if (n === 1) return validateBasicsStep(form);
    if (n === 5) return validateWorkflowStep(form);
    if (n === 6) return validateApplicabilityStep(form);
    return null;
  };

  const savePolicy = async () => {
    const err = validateStep(6) || validateBasicsStep(form);
    if (err) {
      setSaveError(err);
      return;
    }
    setSaving(true);
    try {
      const res = await payrollPolicyService.saveLoanPolicy(formToPayload(form));
      if (res?.success === false) throw new Error(res.message || "Save failed");
      toast.success(isEditing ? "Loan policy updated" : "Loan policy saved");
      setIsFormOpen(false);
      load();
    } catch (e) {
      setSaveError(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const { handleStepClick, footer } = createPolicyWizardNav({
    policyFormStep: step,
    steps: LOAN_POLICY_STEPS,
    validateStep,
    setPolicyFormStep: setStep,
    setPolicySaveError: setSaveError,
    onCancel: () => setIsFormOpen(false),
    onPreviewSave: savePolicy,
    darkMode,
  });

  const openNew = () => {
    setForm(emptyLoanForm());
    setIsEditing(false);
    setStep(1);
    setSaveError(null);
    setIsFormOpen(true);
  };

  const openEdit = (row) => {
    setForm(rowToForm(row));
    setIsEditing(true);
    setStep(1);
    setSaveError(null);
    setActionMenu(null);
    setIsFormOpen(true);
  };

  const remove = async (row) => {
    setActionMenu(null);
    if (!canEdit) return;
    try {
      await payrollPolicyService.deleteLoanPolicy(row.id);
      toast.success("Policy deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.message || "Delete failed");
    }
  };

  const addWorkflowStep = (key) => {
    setForm((prev) => ({
      ...prev,
      [key]: [
        ...(prev[key] || []),
        { role: defaultPolicyRoleValue || "manager", slaDays: 3, notifyOnly: false },
      ],
    }));
  };
  const removeWorkflowStep = (key, index) => {
    setForm((prev) => ({ ...prev, [key]: (prev[key] || []).filter((_, i) => i !== index) }));
  };
  const updateWorkflowStep = (key, index, field, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: (prev[key] || []).map((s, i) => (i === index ? { ...s, [field]: value } : s)),
    }));
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <PolicyListToolbar
        title="Loan policies"
        newLabel="New Loan Policy"
        onNew={openNew}
        searchValue={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        showInactive={showInactive}
        onToggleInactive={() => setShowInactive((v) => !v)}
      />
      {loading ? (
        <p className="py-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">Loading…</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto rounded-xl border border-[hsl(var(--ds-border))]">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]">
              <tr>
                <th className="w-12 px-4 py-3">#</th>
                <th className="px-4 py-3">Policy Name</th>
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Effective</th>
                <th className="px-4 py-3">Approval</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listed.map((row, idx) => (
                <tr key={row.id} className="border-t border-[hsl(var(--ds-border))]">
                  <td className="px-4 py-3">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium">{row.name}</td>
                  <td className="px-4 py-3 capitalize">{row.loanType || row.rulesJson?.loanType}</td>
                  <td className="px-4 py-3">{row.effectiveDate || "—"}</td>
                  <td className="px-4 py-3 capitalize">
                    {(row.workflowJson?.approvalMethod || "workflow").replace("_", " ")}
                  </td>
                  <td className="px-4 py-3">
                    <span className={policyListStatusClass(row.status === "active")}>{row.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      className={policyListRowActionClass}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setActionMenu({
                          row,
                          top: rect.bottom + 4,
                          left: Math.max(8, rect.right - 180),
                        });
                      }}
                    >
                      <MoreVertical size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!listed.length && (
            <p className="py-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
              {search.trim()
                ? "No policies match your search."
                : 'No policies. Click "New Loan Policy" to create one.'}
            </p>
          )}
        </div>
      )}

      {actionMenu && (
        <div className="fixed inset-0 z-[10010]" onClick={() => setActionMenu(null)}>
          <div
            className="absolute z-[10011] w-44 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 shadow-lg"
            style={{ top: actionMenu.top, left: actionMenu.left }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="block w-full px-3 py-2 text-left text-sm hover:bg-[hsl(var(--ds-muted))]"
              onClick={() => openEdit(actionMenu.row)}
            >
              Edit
            </button>
            {canEdit && (
              <button
                type="button"
                className="block w-full px-3 py-2 text-left text-sm text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]"
                onClick={() => remove(actionMenu.row)}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}

      {isFormOpen && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/45 p-4 backdrop-blur-[2px]">
          <div className="flex h-[min(90vh,720px)] w-[min(92vw,780px)] flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
              <h3 className="text-[15px] font-semibold">
                {isEditing ? "Edit loan policy" : "New loan policy"}
              </h3>
              <button type="button" onClick={() => setIsFormOpen(false)}>
                <X size={18} />
              </button>
            </div>
            <div className="flex min-h-0 flex-1 flex-col">
              <PolicyFormWizardShell
                darkMode={darkMode}
                steps={LOAN_POLICY_STEPS}
                currentStep={step}
                onStepClick={handleStepClick}
                policySaveError={saveError}
                footer={
                  <>
                    {footer}
                    {saving && (
                      <span className="w-full text-right text-xs text-[hsl(var(--ds-muted-fg))] sm:w-auto">
                        Saving…
                      </span>
                    )}
                  </>
                }
              >
                {step === 1 && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PolicyHintField label="Policy name" hint="Internal label, e.g. Staff Personal Loan.">
                      <input
                        className={pwInput}
                        value={form.policyName}
                        onChange={(e) => setForm((p) => ({ ...p, policyName: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField
                      label="Effective from"
                      hint="First date this policy can apply. Changes after a finalised payroll month follow sequential unlock."
                    >
                      <input
                        type="date"
                        className={pwInput}
                        value={form.effectiveDate}
                        onChange={(e) => setForm((p) => ({ ...p, effectiveDate: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField
                      label="Loan type"
                      hint="Purpose of the employer loan. This is not Tax Planning 80C / house-property."
                    >
                      <select
                        className={pwInput}
                        value={form.loanType}
                        onChange={(e) => setForm((p) => ({ ...p, loanType: e.target.value }))}
                      >
                        {LOAN_TYPES.map((t) => (
                          <option key={t.value} value={t.value}>
                            {t.label}
                          </option>
                        ))}
                      </select>
                    </PolicyHintField>
                    <PolicyHintField label="Status" hint="Inactive policies are not offered on Apply.">
                      <select
                        className={pwInput}
                        value={form.status}
                        onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </select>
                    </PolicyHintField>
                  </div>
                )}

                {step === 2 && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PolicyHintField
                      label="Minimum service (months)"
                      hint="Counted from Date of Joining. Below this, apply is blocked."
                    >
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.minServiceMonths}
                        onChange={(e) => setForm((p) => ({ ...p, minServiceMonths: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField
                      label="Salary basis for limits"
                      hint="Wage from the current assigned structure used to compute salary-multiple / percent caps."
                    >
                      <select
                        className={pwInput}
                        value={form.salaryBasis}
                        onChange={(e) => setForm((p) => ({ ...p, salaryBasis: e.target.value }))}
                      >
                        <option value="basic">Basic</option>
                        <option value="basic_da">Basic + DA</option>
                        <option value="gross">Gross</option>
                        <option value="ctc">CTC</option>
                      </select>
                    </PolicyHintField>
                    <PolicyHintField label="Limit mode" hint="Fixed ₹, salary × multiple, or salary % — then capped by max ₹ if set.">
                      <select
                        className={pwInput}
                        value={form.limitMode}
                        onChange={(e) => setForm((p) => ({ ...p, limitMode: e.target.value }))}
                      >
                        <option value="amount">Fixed amount</option>
                        <option value="salary_multiple">Salary multiple</option>
                        <option value="salary_percent">Salary percentage</option>
                      </select>
                    </PolicyHintField>
                    <PolicyHintField label="Minimum amount (₹)" hint="Requested amount cannot be below this.">
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.minAmount}
                        onChange={(e) => setForm((p) => ({ ...p, minAmount: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Maximum amount (₹)" hint="Hard cap. Leave blank for no ₹ cap besides salary formula.">
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.maxAmount}
                        onChange={(e) => setForm((p) => ({ ...p, maxAmount: e.target.value }))}
                      />
                    </PolicyHintField>
                    {form.limitMode === "salary_multiple" && (
                      <PolicyHintField label="Salary multiple" hint="Max = salary basis × this number.">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          className={pwInput}
                          value={form.salaryMultiple}
                          onChange={(e) => setForm((p) => ({ ...p, salaryMultiple: e.target.value }))}
                        />
                      </PolicyHintField>
                    )}
                    {form.limitMode === "salary_percent" && (
                      <PolicyHintField label="Salary percent" hint="Max = salary basis × this %.">
                        <input
                          type="number"
                          min="0"
                          className={pwInput}
                          value={form.salaryPercent}
                          onChange={(e) => setForm((p) => ({ ...p, salaryPercent: e.target.value }))}
                        />
                      </PolicyHintField>
                    )}
                    <PolicyHintField
                      label="Max outstanding loans"
                      hint="How many active (not closed) loans the employee may hold under this type."
                    >
                      <input
                        type="number"
                        min="1"
                        className={pwInput}
                        value={form.maxOutstandingLoans}
                        onChange={(e) => setForm((p) => ({ ...p, maxOutstandingLoans: e.target.value }))}
                      />
                    </PolicyHintField>
                  </div>
                )}

                {step === 3 && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PolicyHintField
                      label="Interest mode"
                      hint="Interest-free still may create a taxable perk. Flat = P×rate×tenure. Reducing = amortized EMI."
                    >
                      <select
                        className={pwInput}
                        value={form.interestMode}
                        onChange={(e) => setForm((p) => ({ ...p, interestMode: e.target.value }))}
                      >
                        <option value="interest_free">Interest-free</option>
                        <option value="flat">Flat</option>
                        <option value="reducing">Reducing / amortized</option>
                      </select>
                    </PolicyHintField>
                    <PolicyHintField label="Annual interest rate (%)" hint="Rate charged to the employee.">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={pwInput}
                        value={form.interestRateAnnual}
                        onChange={(e) => setForm((p) => ({ ...p, interestRateAnnual: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField
                      label="Concession rate (%)"
                      hint="If actual rate is below company benchmark, the gap is the perk base. Leave blank if not concessional."
                    >
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={pwInput}
                        value={form.concessionRateAnnual}
                        onChange={(e) => setForm((p) => ({ ...p, concessionRateAnnual: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Min tenure (months)" hint="Requested tenure cannot be shorter.">
                      <input
                        type="number"
                        min="1"
                        className={pwInput}
                        value={form.minTenureMonths}
                        onChange={(e) => setForm((p) => ({ ...p, minTenureMonths: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Max tenure (months)" hint="Requested tenure cannot be longer.">
                      <input
                        type="number"
                        min="1"
                        className={pwInput}
                        value={form.maxTenureMonths}
                        onChange={(e) => setForm((p) => ({ ...p, maxTenureMonths: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Max EMI (₹)" hint="EMI cannot exceed this. Leave blank for no EMI rupee cap.">
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.maxEmiAmount}
                        onChange={(e) => setForm((p) => ({ ...p, maxEmiAmount: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField
                      label="Processing fee"
                      hint="Not part of EMI. None, fixed ₹, or % of sanctioned amount."
                    >
                      <select
                        className={pwInput}
                        value={form.processingFeeMode}
                        onChange={(e) => setForm((p) => ({ ...p, processingFeeMode: e.target.value }))}
                      >
                        <option value="none">None</option>
                        <option value="fixed">Fixed ₹</option>
                        <option value="percent">Percent of amount</option>
                      </select>
                    </PolicyHintField>
                    {form.processingFeeMode !== "none" && (
                      <PolicyHintField label="Fee value" hint="Rupees if fixed, percent if percent mode.">
                        <input
                          type="number"
                          min="0"
                          className={pwInput}
                          value={form.processingFee}
                          onChange={(e) => setForm((p) => ({ ...p, processingFee: e.target.value }))}
                        />
                      </PolicyHintField>
                    )}
                    <PolicyHintField
                      label="First deduction delay (months)"
                      hint="0 = same month as disbursement. 1 = next month (EMI holiday at start)."
                    >
                      <input
                        type="number"
                        min="0"
                        className={pwInput}
                        value={form.firstDeductionDelayMonths}
                        onChange={(e) => setForm((p) => ({ ...p, firstDeductionDelayMonths: e.target.value }))}
                      />
                    </PolicyHintField>
                  </div>
                )}

                {step === 4 && (
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <PolicyHintField
                      label="If net pay is insufficient"
                      hint="Skip = overdue, no cash. Partial = take what is allowed. Carry-forward = add to a later open month. Never auto-close the loan."
                    >
                      <select
                        className={pwInput}
                        value={form.onInsufficientSalary}
                        onChange={(e) => setForm((p) => ({ ...p, onInsufficientSalary: e.target.value }))}
                      >
                        <option value="partial">Partial EMI</option>
                        <option value="skip">Skip EMI</option>
                        <option value="carry_forward">Carry-forward</option>
                      </select>
                    </PolicyHintField>
                    <label className="flex items-center gap-2 text-sm md:col-span-2">
                      <input
                        type="checkbox"
                        checked={!!form.allowMoratorium}
                        onChange={(e) => setForm((p) => ({ ...p, allowMoratorium: e.target.checked }))}
                      />
                      Allow moratorium
                    </label>
                    <PolicyHintField
                      label="Principal recovery head"
                      hint="Salary component / deduction code used when Process Payroll takes EMI principal."
                    >
                      <input
                        className={pwInput}
                        value={form.principalRecovery}
                        onChange={(e) => setForm((p) => ({ ...p, principalRecovery: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Interest recovery head" hint="Deduction code for interest portion of EMI.">
                      <input
                        className={pwInput}
                        value={form.interestRecovery}
                        onChange={(e) => setForm((p) => ({ ...p, interestRecovery: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="F&F recovery head" hint="Used on Full & Final to recover outstanding.">
                      <input
                        className={pwInput}
                        value={form.fnfRecovery}
                        onChange={(e) => setForm((p) => ({ ...p, fnfRecovery: e.target.value }))}
                      />
                    </PolicyHintField>
                    <label className="flex items-center gap-2 text-sm md:col-span-2">
                      <input
                        type="checkbox"
                        checked={!!form.chargePerquisite}
                        onChange={(e) => setForm((p) => ({ ...p, chargePerquisite: e.target.checked }))}
                      />
                      Charge taxable perquisite
                    </label>
                    <PolicyHintField
                      label="Benchmark rate (%)"
                      hint="Company-entered reference rate for Rule 3(7)(i) perk. Not a live RBI feed."
                    >
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={pwInput}
                        value={form.benchmarkRateAnnual}
                        onChange={(e) => setForm((p) => ({ ...p, benchmarkRateAnnual: e.target.value }))}
                      />
                    </PolicyHintField>
                    <PolicyHintField label="Perquisite head" hint="TDS / earning head for the perk amount.">
                      <input
                        className={pwInput}
                        value={form.perquisite}
                        onChange={(e) => setForm((p) => ({ ...p, perquisite: e.target.value }))}
                      />
                    </PolicyHintField>
                  </div>
                )}

                {step === 5 && (
                  <PolicyWorkflowStepSection
                    darkMode={darkMode}
                    policy={form}
                    setPolicy={setForm}
                    workflowStepsTab={workflowTab}
                    setWorkflowStepsTab={setWorkflowTab}
                    policyRoleOptions={policyRoleOptions}
                    addWorkflowStep={addWorkflowStep}
                    removeWorkflowStep={removeWorkflowStep}
                    updateWorkflowStep={updateWorkflowStep}
                    tabs={LOAN_WORKFLOW_TABS}
                    approvalMethodOptions={LOAN_APPROVAL_METHODS}
                  />
                )}

                {step === 6 && (
                  <PolicyApplicabilityStepSection
                    darkMode={darkMode}
                    policy={form}
                    setPolicy={setForm}
                    branchesList={branchesList}
                    departmentsList={departmentsList}
                    designationsList={designationsList}
                    gradesList={gradesList}
                    categoriesList={categoriesList}
                    subDepartmentsList={subDepartmentsList}
                    showLocation={false}
                    showEmploymentTypesHelp={false}
                    onToggleEmploymentType={(value) => {
                      setForm((p) => {
                        const cur = p.employmentTypes || [];
                        return {
                          ...p,
                          employmentTypes: cur.includes(value)
                            ? cur.filter((x) => x !== value)
                            : [...cur, value],
                        };
                      });
                    }}
                  />
                )}
              </PolicyFormWizardShell>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
